import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, addDoc, Timestamp } from 'firebase/firestore';
import { db, functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';
import { useTheme } from '../contexts/ThemeContext';
import toast from 'react-hot-toast';
import { Search, Building, CheckCircle, XCircle, Clock, FileText, Briefcase, Info, X, ExternalLink, RefreshCw } from 'lucide-react';

const CompanyVerifications = () => {
  const { colors } = useTheme();
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsVerification, setDetailsVerification] = useState(null);
  const [secureDocumentUrl, setSecureDocumentUrl] = useState('');
  const [secureDocumentLoading, setSecureDocumentLoading] = useState(false);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });

  useEffect(() => {
    fetchVerifications();
    fetchStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const fetchStats = async () => {
    try {
      const verificationSnapshot = await getDocs(collection(db, 'company_registrations'));
      const statsData = { total: 0, pending: 0, approved: 0, rejected: 0 };

      verificationSnapshot.docs.forEach(doc => {
        const data = doc.data();
        statsData.total++;
        switch (data.status) {
          case 'pending':
            statsData.pending++;
            break;
          case 'approved':
            statsData.approved++;
            break;
          case 'rejected':
            statsData.rejected++;
            break;
          default:
            break;
        }
      });

      setStats(statsData);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to load statistics');
    }
  };

  const fetchVerifications = async () => {
    setLoading(true);
    try {
      let q;
      if (filter === 'all') {
        q = query(collection(db, 'company_registrations'));
      } else {
        q = query(
          collection(db, 'company_registrations'),
          where('status', '==', filter)
        );
      }

      const snapshot = await getDocs(q);

      const verificationsWithUsers = await Promise.all(
        snapshot.docs.map(async (verificationDoc) => {
          const verificationData = verificationDoc.data();
          const userId = verificationData.userId;

          let userData = {};
          try {
            const userDocRef = doc(db, 'users', userId);
            const userDocSnap = await getDoc(userDocRef);
            if (userDocSnap.exists()) {
              userData = userDocSnap.data();
            }
          } catch (userError) {
            console.error('Error fetching user data:', userError);
          }

          return {
            id: verificationDoc.id,
            registrationId: verificationDoc.id,
            userId: userId,
            ...verificationData,
            userData: userData,
            createdAt: verificationData.createdAt?.toDate?.() || new Date(),
            status: verificationData.status || 'pending',
          };
        })
      );

      verificationsWithUsers.sort((a, b) => b.createdAt - a.createdAt);
      setVerifications(verificationsWithUsers);
    } catch (error) {
      console.error('Error fetching verifications:', error);
      toast.error('Failed to load company verifications');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (verification) => {
    try {
      await updateDoc(doc(db, 'company_registrations', verification.registrationId), {
        status: 'approved',
        reviewedAt: Timestamp.now(),
        reviewedBy: 'admin',
      });

      const userUpdateData = {
        userType: 'company',
        companyInfo: omitUndefined({
          companyName: verification.companyName,
          crNumber: verification.crNumber,
          vatNumber: verification.vatNumber,
          contactPerson: verification.contactPerson,
          address: verification.address,
          city: verification.city,
        }),
        updatedAt: Timestamp.now(),
      };

      if (verification.userData?.role === 'guest') {
        userUpdateData.role = 'host';
      }

      if (verification.userData?.verificationStatus !== 'verified') {
        userUpdateData.verificationStatus = 'verified';
        userUpdateData.verifiedAt = Timestamp.now();
        userUpdateData.verifiedBy = 'admin';
      }

      await updateDoc(doc(db, 'users', verification.userId), userUpdateData);

      await createNotification(
        verification.userId,
        'companyVerificationApproved',
        'Company Verification Approved',
        `Congratulations! Your company "${verification.companyName}" has been verified. You can now list properties as a company.`
      );

      fetchVerifications();
      fetchStats();
      toast.success('Company verified successfully!');
    } catch (error) {
      console.error('Error approving verification:', error);
      toast.error('Error approving verification: ' + error.message);
    }
  };

  const handleReject = async () => {
    if (!selectedVerification || !rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    try {
      const isRevoking = selectedVerification.status === 'approved';

      await updateDoc(doc(db, 'company_registrations', selectedVerification.registrationId), {
        status: 'rejected',
        rejectionReason: rejectionReason,
        reviewedAt: Timestamp.now(),
        reviewedBy: 'admin',
      });

      if (isRevoking) {
        await updateDoc(doc(db, 'users', selectedVerification.userId), {
          userType: 'individual',
          companyInfo: null,
          updatedAt: Timestamp.now(),
          revokedAt: Timestamp.now(),
        });

        await createNotification(
          selectedVerification.userId,
          'companyVerificationRevoked',
          'Company Verification Revoked',
          `Your company verification has been revoked. Reason: ${rejectionReason}`
        );
      } else {
        await createNotification(
          selectedVerification.userId,
          'companyVerificationRejected',
          'Company Verification Rejected',
          `Your company registration has been rejected. Reason: ${rejectionReason}. Please review and resubmit with correct information.`
        );
      }

      setShowRejectModal(false);
      setSelectedVerification(null);
      setRejectionReason('');
      fetchVerifications();
      fetchStats();
      toast.success(isRevoking ? 'Company verification revoked successfully' : 'Company verification rejected successfully');
    } catch (error) {
      console.error('Error rejecting verification:', error);
      toast.error('Error rejecting verification: ' + error.message);
    }
  };

  const createNotification = async (userId, type, title, message) => {
    try {
      await addDoc(collection(db, 'notifications'), {
        userId: userId,
        type: type,
        title: title,
        message: message,
        isRead: false,
        createdAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error creating notification:', error);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: colors.warningGhost, color: colors.warning, icon: Clock, text: 'Pending' },
      approved: { bg: colors.successGhost, color: colors.success, icon: CheckCircle, text: 'Approved' },
      rejected: { bg: colors.errorGhost, color: colors.error, icon: XCircle, text: 'Rejected' },
    };

    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;

    return (
      <div 
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold tracking-wide uppercase shadow-sm"
        style={{ backgroundColor: badge.bg, color: badge.color, border: `1px solid ${badge.color}30` }}
      >
        <Icon className="w-3.5 h-3.5" />
        {badge.text}
      </div>
    );
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const omitUndefined = (value) =>
    Object.fromEntries(
      Object.entries(value).filter(([, fieldValue]) => fieldValue !== undefined)
    );

  const getDocumentUrl = (verification) =>
    secureDocumentUrl ||
    verification?.documentImageUrl ||
    verification?.documentUrl ||
    verification?.uploadedDocumentUrl ||
    verification?.registrationDocumentUrl ||
    verification?.companyDocumentUrl ||
    '';

  const openDetailsModal = async (verification) => {
    setDetailsVerification(verification);
    setShowDetailsModal(true);
    setSecureDocumentUrl('');
    setSecureDocumentLoading(true);

    try {
      const getCompanyDocumentAccess = httpsCallable(functions, 'getCompanyDocumentAccess');
      const result = await getCompanyDocumentAccess({
        registrationId: verification.registrationId,
      });
      setSecureDocumentUrl(result.data?.documentUrl || '');
    } catch (error) {
      console.error('Error fetching secure company document URL:', error);
      setSecureDocumentUrl('');
    } finally {
      setSecureDocumentLoading(false);
    }
  };

  const isImageDocument = (url) => /\.(png|jpe?g|webp|gif|bmp|svg)(\?|#|$)/i.test(url || '');

  const filteredVerifications = verifications.filter(c => 
    (c.companyName || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
    (c.crNumber || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const kpis = [
    { id: 'pending', label: 'Pending Review', value: stats.pending, icon: Clock, color: colors.warning, gradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)' },
    { id: 'approved', label: 'Approved', value: stats.approved, icon: CheckCircle, color: colors.success, gradient: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)' },
    { id: 'rejected', label: 'Rejected', value: stats.rejected, icon: XCircle, color: colors.error, gradient: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)' },
    { id: 'all', label: 'Total Companies', value: stats.total, icon: Briefcase, color: colors.textSecondary, gradient: 'linear-gradient(135deg, #64748B 0%, #475569 100%)' },
  ];

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ color: colors.text }}>
            Company Verifications
          </h1>
          <p className="mt-1 text-sm" style={{ color: colors.textSecondary }}>
            Review and manage company registration requests.
          </p>
        </div>
        <button 
          onClick={() => { fetchVerifications(); fetchStats(); }}
          className="px-4 py-2 rounded-xl text-[13px] font-semibold transition-colors flex items-center gap-2"
          style={{ backgroundColor: colors.surfaceHover, color: colors.text, border: `1px solid ${colors.border}` }}
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh Data
        </button>
      </div>

       {/* KPI Cards / Filters */}
       <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {kpis.map((kpi, i) => {
          const isActive = filter === kpi.id;
          return (
            <div
              key={kpi.id}
              className={`card-premium p-4 cursor-pointer transition-all ${isActive ? 'ring-2' : ''} opacity-0 animate-fadeInScale stagger-${i+1}`}
              style={{
                backgroundColor: isActive ? `${kpi.color}10` : colors.card,
                border: `1px solid ${isActive ? kpi.color : colors.border}`,
                boxShadow: isActive ? `0 4px 20px ${kpi.color}20` : undefined,
                ringColor: isActive ? kpi.color : 'transparent'
              }}
              onClick={() => setFilter(kpi.id)}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[12px] font-semibold uppercase tracking-wider mb-1" style={{ color: isActive ? kpi.color : colors.textSecondary }}>{kpi.label}</p>
                  <p className="text-2xl font-bold" style={{ color: colors.text }}>{kpi.value}</p>
                </div>
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm"
                  style={{ background: kpi.gradient }}
                >
                  <kpi.icon className="w-5 h-5 text-white" />
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Search Bar */}
      <div className="card-premium p-4" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
        <div className="relative">
           <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-[18px] h-[18px]" style={{ color: colors.textMuted }} />
           <input
             type="text"
             placeholder="Search by company name or CR number..."
             className="w-full pl-11 pr-4 py-2.5 rounded-xl text-[14px] focus:outline-none transition-all duration-200"
             style={{
               backgroundColor: colors.backgroundSecondary,
               border: `1px solid ${colors.border}`,
               color: colors.text
             }}
             onFocus={(e) => {
               e.target.style.borderColor = colors.borderActive;
               e.target.style.boxShadow = `0 0 0 3px ${colors.primaryGhost}`;
             }}
             onBlur={(e) => {
               e.target.style.borderColor = colors.border;
               e.target.style.boxShadow = 'none';
             }}
             value={searchTerm}
             onChange={(e) => setSearchTerm(e.target.value)}
           />
        </div>
      </div>

       {/* Verifications Table */}
       <div className="card-premium overflow-hidden" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
        {loading ? (
          <div className="flex flex-col items-center justify-center h-64">
             <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: colors.primary }}></div>
             <p className="mt-4 text-sm font-medium" style={{ color: colors.textSecondary }}>Refreshing companies...</p>
          </div>
        ) : filteredVerifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: colors.backgroundSecondary }}>
               <Briefcase className="w-8 h-8" style={{ color: colors.textMuted }} />
            </div>
            <h3 className="text-[15px] font-semibold" style={{ color: colors.text }}>No {filter !== 'all' ? filter : ''} companies found</h3>
            <p className="mt-1 text-[13px]" style={{ color: colors.textSecondary }}>
               Try clearing filters or search terms.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr style={{ backgroundColor: colors.surfaceHover, borderBottom: `1px solid ${colors.border}` }}>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>Company Info</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>Contact Person</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>Location</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>Status</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider text-right" style={{ color: colors.textMuted }}>Quick Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredVerifications.map((company, index) => (
                  <tr 
                    key={company.id} 
                    className="group transition-colors"
                    style={{ borderBottom: index < filteredVerifications.length - 1 ? `1px solid ${colors.border}` : 'none', backgroundColor: colors.surface }}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-xl flex items-center justify-center font-bold text-white shadow-sm" style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }}>
                            <Building className="w-[18px] h-[18px]" />
                        </div>
                        <div>
                          <div className="text-[14px] font-bold" style={{ color: colors.text }}>{company.companyName}</div>
                          <div className="text-[12px] font-medium" style={{ color: colors.textSecondary }}>CR: {company.crNumber}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                       <div className="text-[13px] font-medium" style={{ color: colors.text }}>{company.contactPerson}</div>
                       <div className="text-[12px]" style={{ color: colors.textSecondary }}>{company.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-[13px] font-medium" style={{ color: colors.textSecondary }}>
                       {company.city}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                       {getStatusBadge(company.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                       <button
                         onClick={() => { openDetailsModal(company); }}
                         className="px-4 py-2 rounded-xl text-[12px] font-bold transition-all inline-flex items-center gap-2 hover:shadow-md"
                         style={{ backgroundColor: colors.primary, color: '#fff' }}
                       >
                         <Info className="w-3.5 h-3.5" /> View Details
                       </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Details Modal */}
      {showDetailsModal && detailsVerification && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 lg:p-8 animate-fadeIn" style={{ backgroundColor: 'rgba(11, 17, 32, 0.75)', backdropFilter: 'blur(8px)' }}>
            <div className="rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl relative animate-fadeInScale flex flex-col" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
            <div className="p-6 sm:p-8">
              <div className="flex justify-between items-start mb-6">
                <div className="flex items-center gap-4">
                  <div className="h-14 w-14 rounded-2xl flex items-center justify-center shadow-md text-white" style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }}>
                     <Building className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="text-2xl font-bold" style={{ color: colors.text }}>
                        {detailsVerification.companyName}
                      </h3>
                      {getStatusBadge(detailsVerification.status)}
                    </div>
                    <p className="mt-0.5 text-[14px]" style={{ color: colors.textSecondary }}>
                      Submitted: {formatDate(detailsVerification.createdAt)}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => { setShowDetailsModal(false); setDetailsVerification(null); }}
                  className="p-2.5 rounded-full backdrop-blur-md transition-all hover:bg-black/5"
                  style={{ backgroundColor: colors.backgroundSecondary, color: colors.text }}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="my-6 border-t" style={{ borderColor: colors.border }} />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                
                {/* Left Col */}
                <div className="space-y-8">
                   <div>
                      <h4 className="text-[13px] font-semibold uppercase tracking-wider mb-4 flex items-center gap-2" style={{ color: colors.text }}>
                         <Briefcase className="w-4 h-4" /> Company Information
                      </h4>
                      <div className="space-y-4 p-5 rounded-xl border" style={{ borderColor: colors.border, backgroundColor: colors.backgroundSecondary }}>
                         <div>
                            <p className="text-[11px] uppercase tracking-wider font-semibold mb-1" style={{ color: colors.textMuted }}>CR Number</p>
                            <p className="text-[14px] font-medium font-mono" style={{ color: colors.text }}>{detailsVerification.crNumber}</p>
                         </div>
                         <div>
                            <p className="text-[11px] uppercase tracking-wider font-semibold mb-1" style={{ color: colors.textMuted }}>VAT Number</p>
                            <p className="text-[14px] font-medium font-mono" style={{ color: colors.text }}>{detailsVerification.vatNumber || 'N/A'}</p>
                         </div>
                         <div>
                            <p className="text-[11px] uppercase tracking-wider font-semibold mb-1" style={{ color: colors.textMuted }}>HQ Address</p>
                            <p className="text-[14px] font-medium pr-4" style={{ color: colors.text }}>{detailsVerification.address}, {detailsVerification.city}</p>
                         </div>
                      </div>
                   </div>

                   <div>
                      <h4 className="text-[13px] font-semibold uppercase tracking-wider mb-4 flex items-center gap-2" style={{ color: colors.text }}>
                         <FileText className="w-4 h-4" /> Official Documents
                      </h4>
                      {secureDocumentLoading ? (
                        <div className="p-6 rounded-xl border flex flex-col items-center justify-center text-center" style={{ borderColor: colors.border, backgroundColor: colors.backgroundSecondary }}>
                          <FileText className="w-8 h-8 mb-2 animate-pulse" style={{ color: colors.primary }} />
                          <p className="text-sm font-medium" style={{ color: colors.textSecondary }}>Loading secure document...</p>
                        </div>
                      ) : getDocumentUrl(detailsVerification) ? (
                        <div className="space-y-3">
                           <div className="border rounded-xl overflow-hidden shadow-sm aspect-video flex items-center justify-center relative group" style={{ borderColor: colors.border, backgroundColor: colors.backgroundSecondary }}>
                              {isImageDocument(getDocumentUrl(detailsVerification)) ? (
                                <>
                                  <img src={getDocumentUrl(detailsVerification)} alt="Doc" className="w-full h-full object-contain" />
                                  <button onClick={() => window.open(getDocumentUrl(detailsVerification), '_blank')} className="absolute top-2 right-2 p-2 rounded-lg bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-md">
                                     <ExternalLink className="w-4 h-4" />
                                  </button>
                                </>
                              ) : (
                                <div className="text-center p-4">
                                   <FileText className="w-10 h-10 mb-3 mx-auto" style={{ color: colors.primary }} />
                                   <p className="text-sm font-medium" style={{ color: colors.textSecondary }}>PDF / Non-Image Document</p>
                                </div>
                              )}
                           </div>
                           <button onClick={() => window.open(getDocumentUrl(detailsVerification), '_blank')} className="w-full py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2" style={{ backgroundColor: colors.primary, color: '#fff' }}>
                              <ExternalLink className="w-4 h-4" /> Open Full Document
                           </button>
                        </div>
                      ) : (
                         <div className="p-6 rounded-xl border flex flex-col items-center justify-center text-center" style={{ borderColor: colors.border, backgroundColor: colors.backgroundSecondary }}>
                            <FileText className="w-8 h-8 mb-2" style={{ color: colors.textMuted }} />
                            <p className="text-sm font-medium" style={{ color: colors.textSecondary }}>No Document Provided</p>
                         </div>
                      )}
                   </div>
                </div>

                {/* Right Col */}
                <div className="space-y-8">
                   <div>
                      <h4 className="text-[13px] font-semibold uppercase tracking-wider mb-4 flex items-center gap-2" style={{ color: colors.text }}>
                         <Clock className="w-4 h-4" /> Contact Information
                      </h4>
                      <div className="space-y-4 p-5 rounded-xl border" style={{ borderColor: colors.border, backgroundColor: colors.backgroundSecondary }}>
                         <div>
                            <p className="text-[11px] uppercase tracking-wider font-semibold mb-1" style={{ color: colors.textMuted }}>Contact Person</p>
                            <p className="text-[14px] font-medium" style={{ color: colors.text }}>{detailsVerification.contactPerson}</p>
                         </div>
                         <div>
                            <p className="text-[11px] uppercase tracking-wider font-semibold mb-1" style={{ color: colors.textMuted }}>Email</p>
                            <p className="text-[14px] font-medium" style={{ color: colors.text }}>{detailsVerification.email}</p>
                         </div>
                         <div>
                            <p className="text-[11px] uppercase tracking-wider font-semibold mb-1" style={{ color: colors.textMuted }}>Phone</p>
                            <p className="text-[14px] font-medium" style={{ color: colors.text }}>{detailsVerification.phoneNumber}</p>
                         </div>
                      </div>
                   </div>

                   {detailsVerification.rejectionReason && (
                     <div>
                       <h4 className="text-[13px] font-semibold uppercase tracking-wider mb-4 flex items-center gap-2" style={{ color: colors.error }}>
                          <XCircle className="w-4 h-4" /> Rejection Notes
                       </h4>
                       <div className="p-4 rounded-xl flex gap-3" style={{ backgroundColor: colors.errorGhost, border: `1px solid ${colors.error}40` }}>
                         <p className="text-[14px] font-medium leading-relaxed" style={{ color: colors.error }}>{detailsVerification.rejectionReason}</p>
                       </div>
                     </div>
                   )}
                </div>

              </div>

              {/* Action Buttons */}
              <div className="sticky bottom-0 -mx-6 sm:-mx-8 p-6 sm:p-8 mt-8 rounded-b-2xl border-t bg-opacity-95 backdrop-blur-sm shadow-[0_-8px_30px_rgba(0,0,0,0.05)]" style={{ borderColor: colors.border, backgroundColor: colors.surface }}>
                <div className="flex flex-col sm:flex-row gap-3">
                  {detailsVerification.status === 'pending' && (
                     <>
                        <button
                          onClick={() => { setShowDetailsModal(false); handleApprove(detailsVerification); }}
                          className="flex-1 py-3.5 rounded-xl font-bold text-white shadow-md hover:shadow-lg flex justify-center items-center gap-2 transition-transform hover:-translate-y-0.5"
                          style={{ background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)' }}
                        >
                          <CheckCircle className="w-[18px] h-[18px]" /> Approve Company
                        </button>
                        <button
                          onClick={() => { setShowDetailsModal(false); setSelectedVerification(detailsVerification); setShowRejectModal(true); }}
                          className="flex-1 py-3.5 rounded-xl font-bold text-white shadow-md hover:shadow-lg flex justify-center items-center gap-2 transition-transform hover:-translate-y-0.5"
                          style={{ background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)' }}
                        >
                          <XCircle className="w-[18px] h-[18px]" /> Reject Request
                        </button>
                     </>
                  )}
                  {detailsVerification.status === 'approved' && (
                     <button
                        onClick={() => { setShowDetailsModal(false); setSelectedVerification(detailsVerification); setShowRejectModal(true); }}
                        className="flex-1 py-3.5 rounded-xl font-bold transition-all flex justify-center items-center gap-2"
                        style={{ backgroundColor: colors.errorGhost, color: colors.error, border: `1px solid ${colors.error}40` }}
                      >
                        <XCircle className="w-[18px] h-[18px]" /> Revoke & Demote to Individual
                      </button>
                  )}
                  {detailsVerification.status === 'rejected' && (
                     <button
                        onClick={() => { setShowDetailsModal(false); handleApprove(detailsVerification); }}
                        className="flex-1 py-3.5 rounded-xl font-bold transition-all flex justify-center items-center gap-2"
                        style={{ backgroundColor: colors.primaryGhost, color: colors.primary, border: `1px solid ${colors.primary}` }}
                      >
                        <CheckCircle className="w-[18px] h-[18px]" /> Re-Approve Company
                      </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject/Revoke Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 flex items-center justify-center z-[60] p-4 animate-fadeIn" style={{ backgroundColor: 'rgba(11, 17, 32, 0.8)', backdropFilter: 'blur(8px)' }}>
          <div className="rounded-2xl max-w-md w-full p-6 sm:p-8 animate-fadeInScale shadow-2xl" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: colors.errorGhost }}>
               <XCircle className="w-6 h-6" style={{ color: colors.error }} />
            </div>
            <h3 className="text-xl font-bold" style={{ color: colors.text }}>
              {selectedVerification?.status === 'approved' ? 'Revoke Company' : 'Reject Request'}
            </h3>
            <p className="mt-2 text-[14px] leading-relaxed" style={{ color: colors.textSecondary }}>
              Please provide a detailed reason for {selectedVerification?.status === 'approved' ? 'revoking' : 'rejecting'} {selectedVerification?.companyName}'s {selectedVerification?.status === 'approved' ? 'company verifications. They will be downgraded to a regular user.' : 'request.'}
            </p>

            <textarea
              className="w-full mt-5 p-3.5 rounded-xl text-[14px] focus:outline-none transition-all"
              style={{ backgroundColor: colors.backgroundSecondary, border: `1px solid ${colors.border}`, color: colors.text }}
              rows={4}
              placeholder="e.g. CR document is expired..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              onFocus={(e) => { e.target.style.borderColor = colors.error; e.target.style.boxShadow = `0 0 0 3px ${colors.errorGhost}`; }}
              onBlur={(e) => { e.target.style.borderColor = colors.border; e.target.style.boxShadow = 'none'; }}
            />

            <div className="flex gap-3 mt-8">
              <button
                onClick={() => { setShowRejectModal(false); setSelectedVerification(null); setRejectionReason(''); }}
                className="flex-[1] py-2.5 rounded-xl font-semibold transition-all hover:bg-opacity-80"
                style={{ backgroundColor: colors.background, color: colors.text, border: `1px solid ${colors.border}` }}
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectionReason.trim()}
                className="flex-[2] py-2.5 rounded-xl font-bold text-white transition-all hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-1.5"
                style={{ background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)', boxShadow: '0 4px 14px rgba(239, 68, 68, 0.3)' }}
              >
                 <XCircle className="w-4 h-4"/> 
                 {selectedVerification?.status === 'approved' ? 'Revoke' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyVerifications;
